import { Countertop } from '../../countertops/countertop.class';
import { CountertopSkuAssetData } from '../../countertops/data/countertop-sku-asset-data.class';
import type { CountertopSkuData } from '../../countertops/types';
import { Kitchen } from '../kitchen.class';

export type BacksplashLineItem = {
	sqft: number;
	sku: string;
	hsProductId: string | null;
	description: string;
	pricePerSqFt: number;
	total: number;
	warning: string | null;
};

// The per-material SKU only identifies the finish — backsplash price is always the flat CUSTOM_BACKSPLASH per-sqft rate.
export const getBacksplashLineItemCfn = (kitchen: Kitchen): BacksplashLineItem | null => {
	const { priceBook } = kitchen.context;
	const findCountertopSku = (sku: string): CountertopSkuData | null => (priceBook ? priceBook.findCountertopSku(sku) : CountertopSkuAssetData.findBySku(sku));

	let sqft = 0;
	let hasUnmappedMaterial = false;
	const materialSkus = new Set<string>();
	const unmappedNames = new Set<string>();

	kitchen.design.countertops.children.forEach((countertop: Countertop) => {
		if (countertop.backsplashes.children.length === 0) return;
		countertop.backsplashes.children.forEach((backsplash) => {
			sqft += (backsplash.size.width * backsplash.size.height) / 144;
		});
		const { backsplashSku, displayName } = countertop.material;
		if (backsplashSku) materialSkus.add(backsplashSku);
		else {
			hasUnmappedMaterial = true;
			unmappedNames.add(displayName);
		}
	});

	if (sqft === 0) return null;

	const standard = findCountertopSku('CUSTOM_BACKSPLASH');
	const pricePerSqFt = standard ? standard.msrp : 0;

	const candidateSku = !hasUnmappedMaterial && materialSkus.size === 1 ? [...materialSkus][0] : null;
	const candidateData = candidateSku ? findCountertopSku(candidateSku) : null;

	let warning: string | null = null;
	if (hasUnmappedMaterial) warning = `No backsplash SKU is set up for ${[...unmappedNames].sort().join(', ')} — showing the generic backsplash line instead.`;
	else if (materialSkus.size > 1) warning = 'Backsplashes span multiple countertop materials — showing the generic backsplash line instead.';
	else if (candidateSku && !candidateData) warning = `${candidateSku} is not in the countertop SKU catalog — showing the generic backsplash line instead.`;

	const resolved = candidateData ?? standard;

	return {
		sqft,
		sku: candidateData && candidateSku ? candidateSku : 'CUSTOM_BACKSPLASH',
		hsProductId: resolved?.hsProductId ?? null,
		description: resolved ? resolved.description : 'Custom Backsplash',
		pricePerSqFt,
		total: Math.round(sqft * pricePerSqFt),
		warning
	};
};
